import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useMyCommissions, useMyBalance } from '@/hooks/useCommission'
import { useInstructorCourses } from '@/hooks'
import { useInstructorModules } from '@/hooks/useModules'
import { NotificationList } from '@/components/features/notifications'

interface StatCardProps {
    icon: string
    label: string
    value: string | number
    trend?: string
    trendUp?: boolean
    isLoading?: boolean
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, isLoading }) => (
    <div className="group p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-white/5 hover:border-primary/20 transition-all shadow-sm">
        <div className="flex items-center gap-3 mb-4">
            <div className="size-10 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined text-primary text-[20px]">{icon}</span>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-black">{label}</p>
        </div>
        {isLoading ? (
            <div className="animate-pulse bg-slate-100 dark:bg-white/10 h-8 rounded-lg w-24" />
        ) : (
            <p className="text-slate-900 dark:text-white text-3xl font-black tracking-tight">{value}</p>
        )}
    </div>
)

export const InstructorDashboardPage: React.FC = () => {
    const { user } = useAuth()
    const { data: instructorCourses, isLoading: isLoadingCourses } = useInstructorCourses(user?.id || '')
    const { data: modules, isLoading: isLoadingModules } = useInstructorModules(user?.id || '')
    const { data: commissions, isLoading: isLoadingCommissions } = useMyCommissions(user?.id || '')
    const { data: balance, isLoading: isLoadingBalance } = useMyBalance(user?.id || '')

    const totalCourses = instructorCourses?.length || 0
    const totalModules = modules?.length || 0
    const pendingEarnings = commissions?.filter((c: any) => c.status === 'pending')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const earnedBalance = balance?.earnedBalance || 0

    const formatCurrency = (amount: number) => {
        return `Rp ${amount.toLocaleString('id-ID')}`
    }

    const hour = new Date().getHours()
    const greeting = hour < 11 ? 'Selamat Pagi' : hour < 15 ? 'Selamat Siang' : hour < 18 ? 'Selamat Sore' : 'Selamat Malam'

    return (
        <div className="space-y-10 pb-10">
            {/* Welcome Banner */}
            <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-900 via-slate-800 to-primary/80 dark:from-slate-900 dark:via-slate-800 dark:to-primary/60 p-10 text-white shadow-2xl">
                <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl from-primary/30 to-transparent rounded-full blur-3xl" />
                
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div>
                        <p className="text-white/60 text-sm font-black mb-2">{greeting}</p>
                        <h1 className="text-4xl font-black tracking-tight mb-2">
                            {user?.name || 'Instructor'}
                        </h1>
                        <p className="text-white/50 text-sm font-bold">Akses Instruktur • Pelatihan Merdeka</p>
                    </div>

                    <div className="flex flex-wrap gap-4">
                        <Link
                            to="/instructor/courses"
                            className="px-8 py-4 bg-white text-slate-900 rounded-[1.25rem] font-black text-sm hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl flex items-center gap-2"
                        >
                            <span className="material-symbols-outlined text-[20px]">menu_book</span>
                            Kelola Kursus
                        </Link>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    icon="menu_book"
                    label="Kursus Anda"
                    value={totalCourses}
                    isLoading={isLoadingCourses}
                />
                <StatCard
                    icon="folder"
                    label="Total Modul"
                    value={totalModules}
                    isLoading={isLoadingModules}
                />
                <StatCard
                    icon="account_balance_wallet"
                    label="Saldo Tersedia"
                    value={formatCurrency(earnedBalance)}
                    isLoading={isLoadingBalance}
                />
                <StatCard
                    icon="history"
                    label="Pendapatan Tertahan"
                    value={formatCurrency(pendingEarnings)}
                    isLoading={isLoadingCommissions}
                />
            </div>

            {/* Notifications Section */}
            <div className="rounded-[2rem] bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100 dark:border-white/5 flex items-center justify-between bg-slate-50/30 dark:bg-white/[0.01]">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-primary text-[22px]">notifications</span>
                        <h3 className="text-sm font-black text-slate-900 dark:text-white">Notifikasi Terbaru</h3>
                    </div>
                </div>
                <div className="p-4">
                    <NotificationList userId={user?.id} limit={5} compact={false} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                {/* My Courses */}
                <div className="flex flex-col space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <div className="flex items-center gap-3">
                            <div className="size-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary text-[24px]">school</span>
                            </div>
                            <h3 className="font-black text-slate-900 dark:text-white text-sm">Kursus Aktif</h3>
                        </div>
                        <Link to="/instructor/courses" className="text-sm font-black text-primary hover:underline">Lihat Semua</Link>
                    </div>
                    
                    <div className="flex-1 flex flex-col space-y-4">
                        {isLoadingCourses ? (
                            <div className="flex-1 flex items-center justify-center p-12 text-center text-slate-400 font-black text-xs bg-slate-50/50 dark:bg-white/[0.02] rounded-[2rem] border-2 border-dashed border-slate-100">Sinkronisasi...</div>
                        ) : instructorCourses && instructorCourses.length > 0 ? (
                            instructorCourses.slice(0, 5).map((split: any) => (
                                <div key={split.courseId} className="group p-5 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-white/5 hover:border-primary/20 transition-all shadow-sm flex items-center gap-6">
                                    <div className="size-14 rounded-2xl bg-slate-50 dark:bg-white/5 flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
                                        <span className="material-symbols-outlined text-primary text-[28px]">menu_book</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-black text-slate-900 dark:text-white truncate text-base tracking-tight">{split.courseTitle || 'Course'}</h4>
                                        <div className="flex items-center gap-3 mt-1.5">
                                            <span className="bg-emerald-500 text-white px-2.5 py-1 rounded-full text-xs font-bold">
                                                {split.splitPercentage > 0 ? `${split.splitPercentage}% Split` : 'Fixed'}
                                            </span>
                                            <span className="text-sm font-black text-slate-400">
                                                {split.splitPercentage > 0 ? 'Revenue Share' : 'Flat Rate'}
                                            </span>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/instructor/courses/${split.courseId}/modules`}
                                        className="size-12 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center hover:scale-110 transition-all shadow-lg shrink-0"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                                    </Link>
                                </div>
                            ))
                        ) : (
                            <div className="flex-1 min-h-[320px] py-16 px-8 text-center bg-slate-50/50 dark:bg-white/[0.02] border-2 border-dashed border-slate-200 dark:border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center gap-4 group hover:border-primary/30 transition-colors">
                                <div className="size-20 rounded-3xl bg-white dark:bg-white/5 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                                    <span className="material-symbols-outlined text-slate-200 dark:text-slate-700 text-[48px]">menu_book</span>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-lg font-black text-slate-900 dark:text-white">Belum Ada Kursus</p>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 max-w-[240px] mx-auto">Anda belum memiliki kursus yang ditugaskan saat ini.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* My Modules */}
                <div className="flex flex-col space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <div className="flex items-center gap-3">
                            <div className="size-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
                                <span className="material-symbols-outlined text-indigo-500 text-[24px]">folder</span>
                            </div>
                            <h3 className="font-black text-slate-900 dark:text-white text-sm">Modul Terbaru</h3>
                        </div>
                        <Link to="/instructor/modules" className="text-sm font-black text-indigo-500 hover:underline">Semua</Link>
                    </div>

                    <div className="flex-1 flex flex-col space-y-4">
                        {isLoadingModules ? (
                            <div className="flex-1 flex items-center justify-center p-12 text-center text-slate-400 font-black text-sm bg-slate-50/50 dark:bg-white/[0.02] rounded-[2rem] border-2 border-dashed border-slate-100">Sinkronisasi...</div>
                        ) : modules && modules.length > 0 ? (
                            modules.slice(0, 5).map((module: any) => (
                                <div key={module.id} className="p-5 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-white/5 hover:border-indigo-500/20 transition-all shadow-sm flex items-center gap-6">
                                    <div className="size-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                                        <span className="material-symbols-outlined text-indigo-500 text-[22px]">article</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-base font-black text-slate-900 dark:text-white truncate tracking-tight">{module.title}</p>
                                        <p className="text-sm font-black text-slate-400 mt-1">{module.lesson_count || 0} Materi</p>
                                    </div>
                                    <Link
                                        to={`/instructor/modules/${module.id}/edit`}
                                        className="size-10 rounded-xl bg-slate-50 dark:bg-white/5 text-slate-400 flex items-center justify-center hover:bg-indigo-500/10 hover:text-indigo-500 transition-all shadow-sm"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">edit</span>
                                    </Link>
                                </div>
                            ))
                        ) : (
                            <div className="flex-1 min-h-[320px] py-16 px-8 text-center bg-slate-50/50 dark:bg-white/[0.02] border-2 border-dashed border-slate-200 dark:border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center gap-4 group hover:border-indigo-500/30 transition-colors">
                                <div className="size-20 rounded-3xl bg-white dark:bg-white/5 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                                    <span className="material-symbols-outlined text-slate-200 dark:text-slate-700 text-[48px]">article</span>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-lg font-black text-slate-900 dark:text-white">Modul Kosong</p>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Mulai buat modul pertama Anda.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default InstructorDashboardPage
